/**
 * 写真の送り待ちの列（#117、docs/44 §4「電波の弱い現場」）。
 *   ・撮った写真（表示用 1600px・小さい絵 400px の JPEG）はまずこの列に入れ、順に POST /estimates/:id/photos（multipart）で送る
 *   ・列は IndexedDB に置く＝アプリを閉じても残り、次に開いたときに続きを送る（IndexedDB が使えない窓ではメモリの中だけ）
 *   ・送れない（電波が無い・API が 5xx）ときは止まり、電波が戻ったとき（online）か 15秒あとに続きから。401 は止まるだけ（ログインし直せば続く）
 *   ・送り直しても2枚にならない（clientUuid。API は同じ clientUuid なら前に入れたものを返す）
 *   ・断られた（400・404・409＝大きすぎる・見積が無い・500枚を超える）ものは列から外し、文を知らせる。
 *     #178：知らせはどの画面にいても右下の通知（誤り＝× まで消えない）。前はその見積の画面だけが聞いていて、一覧など別の画面で断られると知らせなく消えた
 * #118：写真の引き出しの「決定」（振り分け）も同じ列に積む（kind: "assign"）。写真のあとに積むので、写真が届いてから送る。
 *   送る直前にいまの見積の版を読み（GET）、版がぶつかったら（409 ESTIMATE_STALE）読み直して3回まで送り直す（決定は「足す」だけなので送り直してよい）。
 *   同じ clientUuid の送り直しは API が前の答えを返す（2回入れない）。送れたら答え（assignId・見積）を知らせる＝画面の「元に戻す」に使う
 * 完全なオフライン（行も作る）は Expo のアプリで（今回は作らない）
 */
import { PHONE_MSG, type PhotoAssign, type PhotoAssignResult } from "@genbaryoku/contract";
import { useSyncExternalStore } from "react";
import { notify } from "../../components/Notice.jsx";
import { productHeaders } from "../../lib/product.js";

export type QueuedPhoto = {
  kind?: "photo";
  clientUuid: string;
  estimateId: string;
  /** 付ける行の系譜（null＝行に入れていない写真） */
  lineLineageId: string | null;
  display: Blob;
  thumb: Blob;
  /** 送る前に見せる小さい絵（data: URL。画面の CSP は blob: を許さない） */
  preview: string;
  createdAt: number;
  /** その見積の件名（断られたときの知らせに出す。#178。前からある列には無い） */
  label?: string;
};
/** 写真の引き出しの決定（#118）。payload は版と clientUuid の無い PhotoAssign */
export type QueuedAssign = {
  kind: "assign";
  clientUuid: string;
  estimateId: string;
  payload: Omit<PhotoAssign, "version" | "clientUuid">;
  createdAt: number;
  /** その見積の件名（断られたときの知らせに出す。#178） */
  label?: string;
};
type QueueItem = QueuedPhoto | QueuedAssign;
const isAssign = (x: QueueItem): x is QueuedAssign => x.kind === "assign";
export type QueueEvent =
  | { kind: "uploaded"; estimateId: string }
  | { kind: "rejected"; estimateId: string; message: string }
  | { kind: "assigned"; estimateId: string; clientUuid: string; result: PhotoAssignResult };

const DB_NAME = "gb-phone";
const STORE = "photo-queue";
let dbp: Promise<IDBDatabase | null> | null = null;
/** IndexedDB が使えないときの置き場（この窓のあいだだけ） */
const memory = new Map<string, QueueItem>();
let snapshot: QueueItem[] = [];
/** いま送っている項目（取り消せない） */
let inFlight: string | null = null;
const listeners = new Set<() => void>();
const eventListeners = new Set<(e: QueueEvent) => void>();
let running = false;
let timer: ReturnType<typeof setTimeout> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbp) return dbp;
  dbp = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const r = indexedDB.open(DB_NAME, 1);
      r.onupgradeneeded = () => {
        if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE, { keyPath: "clientUuid" });
      };
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbp;
}
function tx<T>(db: IDBDatabase, mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = f(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function readAll(): Promise<QueueItem[]> {
  const db = await openDb();
  let items: QueueItem[] = [...memory.values()];
  if (db) {
    try {
      items = items.concat(await tx<QueueItem[]>(db, "readonly", (s) => s.getAll() as IDBRequest<QueueItem[]>));
    } catch {
      /* 読めなければメモリの分だけ */
    }
  }
  return items.sort((a, b) => a.createdAt - b.createdAt);
}
async function refresh(): Promise<void> {
  snapshot = await readAll();
  listeners.forEach((f) => f());
}
async function store(item: QueueItem): Promise<void> {
  const db = await openDb();
  if (db) {
    try {
      await tx(db, "readwrite", (s) => s.put(item));
      return;
    } catch {
      /* 入らなければメモリへ */
    }
  }
  memory.set(item.clientUuid, item);
}
async function remove(id: string): Promise<void> {
  memory.delete(id);
  const db = await openDb();
  if (db) {
    try {
      await tx(db, "readwrite", (s) => s.delete(id));
    } catch {
      /* 無ければよい */
    }
  }
}

const useSnapshot = () =>
  useSyncExternalStore(
    (f) => {
      listeners.add(f);
      return () => listeners.delete(f);
    },
    () => snapshot,
  );
/** 送り待ちの写真（その見積の分。古い順） */
export function usePhotoQueue(estimateId: string): QueuedPhoto[] {
  return useSnapshot().filter((x): x is QueuedPhoto => !isAssign(x) && x.estimateId === estimateId);
}
/** 送り待ちの決定（#118。その見積の分） */
export function useAssignQueue(estimateId: string): QueuedAssign[] {
  return useSnapshot().filter((x): x is QueuedAssign => isAssign(x) && x.estimateId === estimateId);
}
/** 送れた・断られたの知らせ（画面が一覧を読み直す・文を出す） */
export function onPhotoQueue(f: (e: QueueEvent) => void): () => void {
  eventListeners.add(f);
  return () => eventListeners.delete(f);
}

/** 列に入れて送り始める */
export async function enqueuePhoto(item: QueuedPhoto): Promise<void> {
  await store(item);
  await refresh();
  void kickPhotoQueue();
}
/**
 * 断られた写真・振り分けを知らせる（#178）。どの画面にいても出す（列はアプリ全体で1つ＝見積の画面を離れたあとも送る）。
 * 誤りの通知なので × を押すまで消えない。同じ項目の知らせは1つ
 */
function tellRejected(item: QueueItem, reason: string): void {
  const what = isAssign(item) ? PHONE_MSG.queueAssign : PHONE_MSG.queuePhoto;
  const text = (item.label ? PHONE_MSG.queueRejected.replace("{subject}", item.label) : PHONE_MSG.queueRejectedNoSubject).replace("{what}", what).replace("{reason}", reason);
  notify({ text, tone: "error", key: `photo-rejected-${item.clientUuid}`, testId: "photo-rejected" });
}

/** 写真の引き出しの決定を列に入れて送り始める（#118。写真のあとに送る） */
export async function enqueueAssign(item: Omit<QueuedAssign, "kind" | "createdAt">): Promise<void> {
  await store({ ...item, kind: "assign", createdAt: Date.now() });
  await refresh();
  void kickPhotoQueue();
}
/** まだ送っていない決定を列から外す（「元に戻す」）。送っている最中・送ったあとは false */
export async function cancelQueued(clientUuid: string): Promise<boolean> {
  await refresh();
  if (inFlight === clientUuid || !snapshot.some((x) => x.clientUuid === clientUuid)) return false;
  await remove(clientUuid);
  await refresh();
  return true;
}

type Outcome = "ok" | "retry" | "stop" | { rejected: string } | { assigned: PhotoAssignResult };
async function send(item: QueueItem): Promise<Outcome> {
  if (isAssign(item)) return sendAssign(item);
  const fd = new FormData();
  fd.append("clientUuid", item.clientUuid);
  if (item.lineLineageId) fd.append("lineLineageId", item.lineLineageId);
  fd.append("display", item.display, "display.jpg");
  fd.append("thumb", item.thumb, "thumb.jpg");
  let res: Response;
  try {
    res = await fetch(`/api/v1/estimates/${item.estimateId}/photos`, { method: "POST", credentials: "include", headers: productHeaders(), body: fd });
  } catch {
    return "retry"; // 電波が無い
  }
  if (res.ok) return "ok";
  if (res.status === 401) return "stop";
  if (res.status >= 500 || res.status === 429) return "retry";
  const p = (await res.json().catch(() => null)) as { title?: string } | null;
  return { rejected: p?.title ?? "写真を送れませんでした" };
}

/** 決定を送る：いまの版を読んでから送る。版がぶつかったら読み直して3回まで */
async function sendAssign(item: QueuedAssign): Promise<Outcome> {
  const base = `/api/v1/estimates/${item.estimateId}`;
  for (let i = 0; i < 3; i++) {
    let res: Response;
    try {
      res = await fetch(base, { credentials: "include", headers: productHeaders() });
    } catch {
      return "retry";
    }
    if (res.status === 401) return "stop";
    if (res.status >= 500 || res.status === 429) return "retry";
    if (!res.ok) return { rejected: ((await res.json().catch(() => null)) as { title?: string } | null)?.title ?? "振り分けを送れませんでした" };
    const est = (await res.json()) as { updatedAt: string };
    try {
      res = await fetch(`${base}/photo-assign`, {
        method: "POST",
        credentials: "include",
        headers: { ...productHeaders(), "content-type": "application/json" },
        body: JSON.stringify({ ...item.payload, version: est.updatedAt, clientUuid: item.clientUuid }),
      });
    } catch {
      return "retry";
    }
    if (res.ok) return { assigned: (await res.json()) as PhotoAssignResult };
    if (res.status === 401) return "stop";
    if (res.status >= 500 || res.status === 429) return "retry";
    const p = (await res.json().catch(() => null)) as { title?: string; code?: string } | null;
    if (res.status === 409 && p?.code === "ESTIMATE_STALE") continue;
    return { rejected: p?.title ?? "振り分けを送れませんでした" };
  }
  return "retry";
}

/** 列の頭から順に送る（同時に1つ）。止まったら online か 15秒あとに続きから */
export async function kickPhotoQueue(): Promise<void> {
  if (running) return;
  running = true;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  try {
    // 送っているあいだに足された写真も続けて送る（頭から1つずつ）
    for (;;) {
      await refresh();
      const item = snapshot[0];
      if (!item) break;
      inFlight = item.clientUuid;
      const r = await send(item).finally(() => {
        inFlight = null;
      });
      if (r === "ok" || typeof r === "object") {
        await remove(item.clientUuid);
        await refresh();
        const ev: QueueEvent =
          r === "ok"
            ? { kind: "uploaded", estimateId: item.estimateId }
            : "assigned" in r
              ? { kind: "assigned", estimateId: item.estimateId, clientUuid: item.clientUuid, result: r.assigned }
              : { kind: "rejected", estimateId: item.estimateId, message: r.rejected };
        if (ev.kind === "rejected") tellRejected(item, ev.message);
        eventListeners.forEach((f) => f(ev));
        continue;
      }
      if (r === "retry") timer = setTimeout(() => void kickPhotoQueue(), 15_000);
      break;
    }
  } finally {
    running = false;
  }
}

if (typeof window !== "undefined") window.addEventListener("online", () => void kickPhotoQueue());
